'use client';

import { useStatusLocation } from 'hooks/statekeeping';

import { useRobotLocation } from 'lib/ros-abstraction/telemetry/robot-state/hooks';
import { useRobotIdentity } from './useRobotIdentity';

export type GpsCoordinates = {
  latitude: number;
  longitude: number;
};

/**
 * Unified location hook — returns GPS coordinates regardless of robot type.
 * - ROS-abstraction robots: reads from `${deviceName}:Reported:Location` (robot_interfaces/Location)
 * - Legacy robots (Spot, DJI): reads from `${deviceName}:Reported:Status` -> $.location
 */
export function useLocation(): { location: GpsCoordinates; hasLocation: boolean } {
  const { usesRosAbstraction } = useRobotIdentity();

  // Both hooks must always be called (React rules of hooks)
  const { location: statusLocation, hasLocation: statusHasLocation } = useStatusLocation();
  const { location: rosLocation, hasLocation: rosHasLocation } = useRobotLocation();

  if (usesRosAbstraction) {
    return {
      location: {
        latitude: rosLocation?.gps?.latitude ?? 0,
        longitude: rosLocation?.gps?.longitude ?? 0,
      },
      hasLocation: rosHasLocation,
    };
  }

  return {
    location: {
      latitude: statusLocation.latitude,
      longitude: statusLocation.longitude,
    },
    hasLocation: statusHasLocation,
  };
}
